import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Plus, Search, Calendar, Clock, User, Phone, Mail,
  CheckCircle, AlertTriangle, TrendingUp, Euro,
  ChevronRight, Target, Award, GraduationCap, BookOpen,
  Users, FileText, Star, Edit2, Trash2, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de formations équines
const TYPES_FORMATIONS = [
  { 
    id: 'initiation_equitation', 
    nom: 'Initiation Équitation', 
    duree: '10h',
    description: 'Premiers pas à cheval, sécurité, bases',
    niveau: 'debutant',
    tarif: 350
  },
  { 
    id: 'perfectionnement', 
    nom: 'Perfectionnement', 
    duree: '20h',
    description: 'Améliorer sa technique, nouvelles disciplines',
    niveau: 'intermediaire',
    tarif: 600
  },
  { 
    id: 'ethologie', 
    nom: 'Éthologie Équine', 
    duree: '15h',
    description: 'Comprendre le comportement du cheval',
    niveau: 'tous',
    tarif: 450
  },
  { 
    id: 'soins_base', 
    nom: 'Soins de Base', 
    duree: '8h',
    description: 'Pansage, alimentation, premiers soins',
    niveau: 'debutant',
    tarif: 250
  },
  { 
    id: 'debourrage_formation', 
    nom: 'Formation au Débourrage', 
    duree: '40h',
    description: 'Apprendre à débourrer un jeune cheval',
    niveau: 'avance',
    tarif: 1200
  },
  { 
    id: 'attelage_initiation', 
    nom: 'Initiation Attelage', 
    duree: '12h',
    description: 'Bases de la conduite attelée',
    niveau: 'debutant',
    tarif: 400
  },
  { 
    id: 'marechalerie', 
    nom: 'Initiation Maréchalerie', 
    duree: '6h',
    description: 'Entretien des pieds, parage de base',
    niveau: 'intermediaire',
    tarif: 200
  },
  { 
    id: 'travail_sol', 
    nom: 'Travail au Sol', 
    duree: '10h',
    description: 'Longe, liberté, désensibilisation',
    niveau: 'tous',
    tarif: 300
  }
];

const NIVEAUX = [
  { id: 'debutant', nom: 'Débutant', color: 'bg-green-100 text-green-800' },
  { id: 'intermediaire', nom: 'Intermédiaire', color: 'bg-blue-100 text-blue-800' },
  { id: 'avance', nom: 'Avancé', color: 'bg-purple-100 text-purple-800' },
  { id: 'tous', nom: 'Tous niveaux', color: 'bg-gray-100 text-gray-800' }
];

const STATUTS_SESSION = [
  { value: 'planifiee', label: 'Planifiée', color: 'bg-blue-500' },
  { value: 'en_cours', label: 'En cours', color: 'bg-amber-500' },
  { value: 'terminee', label: 'Terminée', color: 'bg-green-500' },
  { value: 'annulee', label: 'Annulée', color: 'bg-red-500' }
];

export default function FormationsEquinesPage() {
  const { activeSociete } = useWorkspace();
  const [activeTab, setActiveTab] = useState('sessions');
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [stagiaires, setStagiaires] = useState([]);
  const [stats, setStats] = useState({});
  const [showAddSessionModal, setShowAddSessionModal] = useState(false);
  const [showAddStagiaireModal, setShowAddStagiaireModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [newSession, setNewSession] = useState({
    formation_id: '',
    date_debut: '',
    date_fin: '',
    formateur: '',
    places_max: 6,
    tarif: 0,
    lieu: '',
    notes: ''
  });

  const [newStagiaire, setNewStagiaire] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    email: '',
    niveau_actuel: 'debutant',
    objectifs: '',
    session_id: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'X-Societe-Id': activeSociete?.id || ''
    };

    try {
      // Fetch sessions
      const resSessions = await fetch(`${API_URL}/api/equin/formations/sessions`, { headers });
      if (resSessions.ok) {
        const data = await resSessions.json();
        setSessions(data.sessions || []);
        setStats(data.stats || {});
      }

      // Fetch stagiaires
      const resStagiaires = await fetch(`${API_URL}/api/equin/formations/stagiaires`, { headers });
      if (resStagiaires.ok) {
        const data = await resStagiaires.json();
        setStagiaires(data.stagiaires || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  }, [activeSociete]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateSession = async () => {
    const token = localStorage.getItem('token');
    try {
      const formation = TYPES_FORMATIONS.find(f => f.id === newSession.formation_id);
      const res = await fetch(`${API_URL}/api/equin/formations/sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': activeSociete?.id || ''
        },
        body: JSON.stringify({
          ...newSession,
          formation_nom: formation?.nom,
          tarif: newSession.tarif || formation?.tarif || 0
        })
      });

      if (res.ok) {
        toast.success('Session créée avec succès');
        setShowAddSessionModal(false);
        setNewSession({
          formation_id: '', date_debut: '', date_fin: '', formateur: '',
          places_max: 6, tarif: 0, lieu: '', notes: ''
        });
        fetchData();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleCreateStagiaire = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/equin/formations/stagiaires`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': activeSociete?.id || ''
        },
        body: JSON.stringify(newStagiaire)
      });

      if (res.ok) {
        toast.success('Stagiaire inscrit');
        setShowAddStagiaireModal(false);
        setNewStagiaire({
          nom: '', prenom: '', telephone: '', email: '',
          niveau_actuel: 'debutant', objectifs: '', session_id: ''
        });
        fetchData();
      } else {
        toast.error('Erreur lors de l\'inscription');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const filteredSessions = sessions.filter(s =>
    s.formation_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.formateur?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (statut) => {
    const s = STATUTS_SESSION.find(st => st.value === statut);
    return s ? <Badge className={`${s.color} text-white`}>{s.label}</Badge> : <Badge>{statut}</Badge>;
  };

  const getNiveauBadge = (niveau) => {
    const n = NIVEAUX.find(niv => niv.id === niveau);
    return n ? <Badge className={n.color}>{n.nom}</Badge> : <Badge>{niveau}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-6" data-testid="formations-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-emerald-900">Formations Équines</h1>
            <p className="text-emerald-700">Gestion des formations, stages et stagiaires</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowAddStagiaireModal(true)} variant="outline">
              <User className="w-4 h-4 mr-2" /> Inscrire stagiaire
            </Button>
            <Button onClick={() => setShowAddSessionModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle session
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Sessions planifiées</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.sessions_planifiees || 0}</p>
              </div>
              <Calendar className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Stagiaires inscrits</p>
                <p className="text-2xl font-bold text-blue-600">{stagiaires.length}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Formations terminées</p>
                <p className="text-2xl font-bold text-green-600">{stats.terminees || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">CA ce mois</p>
                <p className="text-2xl font-bold text-amber-600">{stats.ca_mensuel || 0} €</p>
              </div>
              <Euro className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white/80">
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
          <TabsTrigger value="catalogue">Catalogue</TabsTrigger>
          <TabsTrigger value="stagiaires">Stagiaires</TabsTrigger>
          <TabsTrigger value="certificats">Certificats</TabsTrigger>
        </TabsList>

        <TabsContent value="sessions">
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Sessions de formation</CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Chargement...</div>
              ) : filteredSessions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <GraduationCap className="w-12 h-12 mx-auto mb-4 text-emerald-300" />
                  <p>Aucune session planifiée</p>
                  <Button onClick={() => setShowAddSessionModal(true)} variant="outline" className="mt-4">
                    <Plus className="w-4 h-4 mr-2" /> Créer une session
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredSessions.map((session) => (
                    <div
                      key={session.id}
                      className="border rounded-lg p-4 hover:bg-emerald-50 transition"
                      data-testid={`session-${session.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                            <GraduationCap className="w-6 h-6 text-emerald-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{session.formation_nom}</h3>
                            <p className="text-sm text-gray-500">
                              <User className="w-3 h-3 inline mr-1" />
                              Formateur: {session.formateur || 'Non assigné'}
                            </p>
                            <p className="text-sm text-gray-500">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {session.date_debut ? new Date(session.date_debut).toLocaleDateString('fr-FR') : '-'}
                              {session.date_fin && ` → ${new Date(session.date_fin).toLocaleDateString('fr-FR')}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            {getStatusBadge(session.statut)}
                            <p className="text-sm text-gray-500 mt-1">
                              {session.inscrits || 0}/{session.places_max} places
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-emerald-600">{session.tarif} €</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="catalogue">
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <CardTitle>Catalogue des formations</CardTitle>
              <CardDescription>Formations disponibles</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {TYPES_FORMATIONS.map((formation) => (
                  <div key={formation.id} className="border rounded-lg p-4 hover:shadow-md transition">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold">{formation.nom}</h3>
                      {getNiveauBadge(formation.niveau)}
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{formation.description}</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center text-gray-500">
                        <Clock className="w-3 h-3 mr-1" /> {formation.duree}
                      </span>
                      <span className="font-bold text-emerald-600">{formation.tarif} €</span>
                    </div>
                    <Button 
                      className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700"
                      size="sm"
                      onClick={() => {
                        setNewSession({ ...newSession, formation_id: formation.id, tarif: formation.tarif });
                        setShowAddSessionModal(true);
                      }}
                    >
                      Planifier une session
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stagiaires">
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Stagiaires</CardTitle>
                <Button onClick={() => setShowAddStagiaireModal(true)} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Inscrire
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {stagiaires.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-emerald-300" />
                  <p>Aucun stagiaire inscrit</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {stagiaires.map((stagiaire) => (
                    <div key={stagiaire.id} className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium">{stagiaire.prenom} {stagiaire.nom}</p>
                          <p className="text-sm text-gray-500">{stagiaire.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getNiveauBadge(stagiaire.niveau_actuel)}
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="certificats">
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <CardTitle>Certificats délivrés</CardTitle>
              <CardDescription>Attestations et diplômes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                <Award className="w-12 h-12 mx-auto mb-4 text-amber-400" />
                <p>Les certificats seront générés ici</p>
                <p className="text-sm">Après validation des formations</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Ajout Session */}
      <Dialog open={showAddSessionModal} onOpenChange={setShowAddSessionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouvelle session de formation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Formation *</Label>
              <Select value={newSession.formation_id} onValueChange={(v) => {
                const formation = TYPES_FORMATIONS.find(f => f.id === v);
                setNewSession({ ...newSession, formation_id: v, tarif: formation?.tarif || 0 });
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une formation" />
                </SelectTrigger>
                <SelectContent>
                  {TYPES_FORMATIONS.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.nom} ({f.duree})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={newSession.date_debut}
                  onChange={(e) => setNewSession({ ...newSession, date_debut: e.target.value })}
                />
              </div>
              <div>
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={newSession.date_fin}
                  onChange={(e) => setNewSession({ ...newSession, date_fin: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Formateur</Label>
                <Input
                  value={newSession.formateur}
                  onChange={(e) => setNewSession({ ...newSession, formateur: e.target.value })}
                  placeholder="Nom du formateur"
                />
              </div>
              <div>
                <Label>Places max</Label>
                <Input
                  type="number"
                  value={newSession.places_max}
                  onChange={(e) => setNewSession({ ...newSession, places_max: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tarif (€)</Label>
                <Input
                  type="number"
                  value={newSession.tarif}
                  onChange={(e) => setNewSession({ ...newSession, tarif: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Lieu</Label>
                <Input
                  value={newSession.lieu}
                  onChange={(e) => setNewSession({ ...newSession, lieu: e.target.value })}
                  placeholder="Lieu de formation"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newSession.notes}
                onChange={(e) => setNewSession({ ...newSession, notes: e.target.value })}
                placeholder="Informations complémentaires..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSessionModal(false)}>Annuler</Button>
            <Button onClick={handleCreateSession} className="bg-emerald-600 hover:bg-emerald-700">
              Créer la session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Ajout Stagiaire */}
      <Dialog open={showAddStagiaireModal} onOpenChange={setShowAddStagiaireModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Inscrire un stagiaire</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom *</Label>
                <Input
                  value={newStagiaire.prenom}
                  onChange={(e) => setNewStagiaire({ ...newStagiaire, prenom: e.target.value })}
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  value={newStagiaire.nom}
                  onChange={(e) => setNewStagiaire({ ...newStagiaire, nom: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={newStagiaire.telephone}
                  onChange={(e) => setNewStagiaire({ ...newStagiaire, telephone: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newStagiaire.email}
                  onChange={(e) => setNewStagiaire({ ...newStagiaire, email: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Niveau actuel</Label>
              <Select value={newStagiaire.niveau_actuel} onValueChange={(v) => setNewStagiaire({ ...newStagiaire, niveau_actuel: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NIVEAUX.map(n => (
                    <SelectItem key={n.id} value={n.id}>{n.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Session (optionnel)</Label>
              <Select value={newStagiaire.session_id} onValueChange={(v) => setNewStagiaire({ ...newStagiaire, session_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une session" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Aucune session</SelectItem>
                  {sessions.filter(s => s.statut === 'planifiee').map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.formation_nom} - {new Date(s.date_debut).toLocaleDateString('fr-FR')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Objectifs</Label>
              <Textarea
                value={newStagiaire.objectifs}
                onChange={(e) => setNewStagiaire({ ...newStagiaire, objectifs: e.target.value })}
                placeholder="Objectifs du stagiaire..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStagiaireModal(false)}>Annuler</Button>
            <Button onClick={handleCreateStagiaire} className="bg-emerald-600 hover:bg-emerald-700">
              Inscrire
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
